import { NavigableModal } from '$components'
import { useNavigate, useParams } from 'react-router-dom'
import { EditTeamMemberDetailsForm, EditTeamMemberDetailsSchema, Input } from '$features/team/forms/edit-team-member-details/index'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { userShowOptions, userShowQueryKey, userUpdateMutation } from '$api-client/@tanstack/react-query.gen'
import { Role } from '$api-client/types.gen'
import { ToastHelper } from '$components'

const roleOptions = [
  { id: Role.ADMIN, value: 'Admin' },
  { id: Role.GROOMER, value: 'Groomer' },
  { id: Role.RECEPTIONIST, value: 'Receptionist' },
]

export function EditTeamMemberDetailsModal() {
  const navigate = useNavigate()
  const { id } = useParams()
  const queryClient = useQueryClient()

  const { data } = useQuery({
    ...userShowOptions({
      path: {
        id: Number(id),
      },
      query: {
        include_deleted: true,
      },
    }),
  })

  const { mutate: updateTeamMember, isPending: isUpdatingTeamMember } = useMutation({
    ...userUpdateMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: userShowQueryKey({
          path: {
            id: Number(id),
          },
        }),
      })
      ToastHelper.success('Team member details updated successfully.')
      handleClose()
    },
    onError: () => ToastHelper.error('Failed to update team member details.'),
  })

  const handleClose = () => navigate(-1)

  const handleSubmit = async (values: EditTeamMemberDetailsSchema) => {
    updateTeamMember({
      path: {
        id: Number(id),
      },
      body: {
        role: values[Input.Role].value as ('Admin' | 'Groomer' | 'Receptionist'),
      },
    })
  }

  const teamMember = data?.data

  if (!teamMember) {
    return null
  }

  const currentRole = roleOptions.find((role) => role.id === teamMember.role)

  if (!currentRole) {
    return null
  }

  return (
    <EditTeamMemberDetailsForm
      onSubmit={handleSubmit}
      onCancel={handleClose}
      isLoading={isUpdatingTeamMember}
      defaultValues={{
        [Input.Role]: {
          id: currentRole.id.toString(),
          value: currentRole.value,
        },
      }}
    >
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>
            Edit Team Member Details
          </NavigableModal.Heading>
        </NavigableModal.Header>

        <NavigableModal.Content>
          <EditTeamMemberDetailsForm.Content />
        </NavigableModal.Content>

        <NavigableModal.Footer>
          <EditTeamMemberDetailsForm.Controls />
        </NavigableModal.Footer>
      </NavigableModal>
    </EditTeamMemberDetailsForm>
  )
}
